import { Check, Paperclip, Sparkles, X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Modal } from '@/components/Modal'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { useToast } from '@/hooks/useToast'
import { useAuth } from '@/auth/useAuth'
import { formatDate } from '@/lib/locale'
import {
  useConfirmItem,
  useCreateBillDraft,
  useDismissItem,
  useInboxEmail,
  useReextractEmail,
} from '@/features/inbox/queries'
import type { EmailItemSummary } from '@/features/inbox/types'

function plainBody(text: string | null, html: string | null): string {
  if (text && text.trim()) return text
  if (html && html.trim()) {
    return html
      .replace(/<style[\s\S]*?<\/style>/gi, ' ')
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
  }
  return ''
}

function money(amount: EmailItemSummary['total_amount'], currency: string | null): string {
  if (amount == null) return '-'
  const n = typeof amount === 'string' ? Number(amount) : amount
  if (!Number.isFinite(n)) return String(amount)
  const code = currency && currency.trim() ? `${currency.toUpperCase()} ` : ''
  return `${code}${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

interface EmailDetailModalProps {
  emailId: string | null
  onClose: () => void
  onOpenAttachment: (attachmentId: string, mime: string | null) => void
}

export function EmailDetailModal({ emailId, onClose, onOpenAttachment }: EmailDetailModalProps) {
  const { hasPermission } = useAuth()
  const { toast } = useToast()
  const navigate = useNavigate()
  const emailQuery = useInboxEmail(emailId)
  const confirmItem = useConfirmItem()
  const dismissItem = useDismissItem()
  const createDraft = useCreateBillDraft()
  const reextract = useReextractEmail()

  const email = emailQuery.data?.email ?? null
  const item = email?.item ?? null
  const canManageItems = hasPermission('inbox.items.manage')
  const canSync = hasPermission('inbox.sync')
  const canCreateBill = hasPermission('invoicing.bills.create')
  const busy = confirmItem.isPending || dismissItem.isPending || createDraft.isPending

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn()
      toast({ title: ok })
      onClose()
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Action failed'
      toast({ title: 'Could not complete', description: msg, variant: 'destructive' })
    }
  }

  const onAcceptDraft = async () => {
    if (!item) return
    try {
      await confirmItem.mutateAsync(item.id)
      const res = await createDraft.mutateAsync(item.id)
      toast({ title: 'Accepted', description: 'Draft bill created.' })
      onClose()
      if (res.bill_id) navigate(`/invoicing/bills/${res.bill_id}`)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Action failed'
      toast({ title: 'Could not create draft', description: msg, variant: 'destructive' })
    }
  }

  return (
    <Modal
      open={!!emailId}
      onOpenChange={(open) => !open && onClose()}
      title={email?.subject || 'Email'}
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      {emailQuery.isLoading ? (
        <div className="flex justify-center py-10">
          <LoadingSpinner />
        </div>
      ) : !email ? (
        <p className="py-6 text-sm text-muted-foreground">Email not found.</p>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="font-medium">{email.from_email || 'Unknown sender'}</span>
            {email.system_category ? (
              <Badge variant="secondary">{email.system_category}</Badge>
            ) : null}
            <span className="text-muted-foreground">
              {email.internal_date ? formatDate(email.internal_date) : ''}
            </span>
          </div>

          {item ? (
            <div className="rounded-md border border-border bg-muted/40 p-3">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <Badge variant="brand">{item.kind}</Badge>
                <span className="text-sm font-medium">
                  {item.vendor_name || email.from_email || '-'}
                </span>
                {item.invoice_number ? (
                  <span className="text-sm text-muted-foreground">#{item.invoice_number}</span>
                ) : null}
                <span className="text-sm">{money(item.total_amount, item.currency)}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {canManageItems && item.status !== 'confirmed' ? (
                  <Button
                    size="sm"
                    onClick={() => act(() => confirmItem.mutateAsync(item.id), 'Accepted')}
                    disabled={busy}
                  >
                    <Check className="size-4" />
                    Accept
                  </Button>
                ) : null}
                {canManageItems &&
                canCreateBill &&
                (item.kind === 'invoice' || item.kind === 'bill') &&
                item.status !== 'confirmed' ? (
                  <Button size="sm" variant="secondary" onClick={onAcceptDraft} disabled={busy}>
                    Accept &amp; create draft bill
                  </Button>
                ) : null}
                {canManageItems && item.status !== 'dismissed' ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => act(() => dismissItem.mutateAsync(item.id), 'Dismissed')}
                    disabled={busy}
                  >
                    <X className="size-4 text-destructive" />
                    Dismiss
                  </Button>
                ) : null}
                {canSync ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      act(() => reextract.mutateAsync(email.id), 'Re-extraction queued')
                    }
                    disabled={reextract.isPending}
                  >
                    <Sparkles className="size-4" />
                    Re-extract
                  </Button>
                ) : null}
              </div>
            </div>
          ) : null}

          {email.attachments.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {email.attachments.map((a) => (
                <Button
                  key={a.id}
                  variant="ghost"
                  size="sm"
                  className="h-7 gap-1 px-2"
                  onClick={() => onOpenAttachment(a.id, a.mime_type)}
                >
                  <Paperclip className="size-3.5" />
                  <span className="max-w-[200px] truncate text-xs">{a.filename || 'file'}</span>
                </Button>
              ))}
            </div>
          ) : null}

          <div className="max-h-[50vh] overflow-y-auto whitespace-pre-wrap rounded-md border border-border bg-card p-3 text-sm leading-relaxed">
            {plainBody(email.body_text, email.body_html) || (
              <span className="text-muted-foreground">
                {email.snippet || 'No readable text in this email.'}
              </span>
            )}
          </div>
        </div>
      )}
    </Modal>
  )
}
